import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import {
  MarketplaceContract,
  MarketplaceContractCreate,
  MarketplaceContractDetail,
  MarketplaceContractPaymentCreate,
  MarketplaceContractUpdate,
} from '@core/types/marketplace-contract.type';
import { MarketplaceContractService } from '@core/services';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class MarketplaceContractQuery {
  private readonly service = inject(MarketplaceContractService);
  private readonly queryClient = inject(QueryClient);

  useContractsGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.marketplaceContracts.grid(c),
        queryFn: () => lastValueFrom(this.service.grid(c)),
        select: (res: ApiResponse<PaginationResult<MarketplaceContract>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useContractDetail(id: () => string | null) {
    return injectQuery(() => {
      const v = id();
      return {
        queryKey: QUERY_KEYS.marketplaceContracts.detail(v || ''),
        queryFn: () => lastValueFrom(this.service.get(v!)),
        select: (res: ApiResponse<MarketplaceContractDetail>) => res.data,
        enabled: !!v,
      };
    });
  }

  useCreateContract() {
    return injectMutation(() => ({
      mutationFn: (data: MarketplaceContractCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quotations.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses.all });
      },
    }));
  }

  useUpdateContract() {
    return injectMutation(() => ({
      mutationFn: (data: MarketplaceContractUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quotations.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses.all });
      },
    }));
  }

  useDeleteContract() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quotations.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses.all });
      },
    }));
  }

  useAddContractPayment() {
    return injectMutation(() => ({
      mutationFn: (vars: { contractId: string; data: MarketplaceContractPaymentCreate }) =>
        lastValueFrom(this.service.addPayment(vars.contractId, vars.data)),
      onSuccess: (_res, vars) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.detail(vars.contractId) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
      },
    }));
  }

  useDeleteContractPayment() {
    return injectMutation(() => ({
      mutationFn: (vars: { contractId: string; paymentId: string }) =>
        lastValueFrom(this.service.deletePayment(vars.contractId, vars.paymentId)),
      onSuccess: (_res, vars) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.detail(vars.contractId) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.marketplaceContracts.all });
      },
    }));
  }
}

